import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useQuote, useSendQuote, useDeleteQuote, useConvertQuote, useAcceptQuote } from '../../hooks/useApi';
import { api } from '../../services/api';
import type { QuoteStatus, QuotePriceLine, QuoteActivity, QuoteActivityKind } from '../../types';

const statusConfig: Record<QuoteStatus, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  DRAFT: { label: 'Kladde', color: 'default' },
  SENT: { label: 'Sendt', color: 'info' },
  ACCEPTED: { label: 'Accepteret', color: 'success' },
  REJECTED: { label: 'Afvist', color: 'error' },
  CONVERTED: { label: 'Konverteret', color: 'success' },
  EXPIRED: { label: 'Udløbet', color: 'warning' },
};

const cargoTypeLabels: Record<string, string> = {
  PALLET_GOODS: 'Pallegods', GENERAL_CARGO: 'Stykgods', COOL: 'Køl', FREEZE: 'Frost',
  BULK_CARGO: 'Bulk', CONTAINER: 'Container', MACHINES: 'Maskiner', PACKAGES: 'Pakker', MISCELLANEOUS: 'Diverse',
};

function fmt(n: number, cur: string) {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: cur, minimumFractionDigits: 2 }).format(n);
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: quote, isLoading, error } = useQuote(id || '');
  const sendQuote = useSendQuote();
  const deleteQuote = useDeleteQuote();
  const convertQuote = useConvertQuote();
  const acceptQuote = useAcceptQuote();

  const [confirmDialog, setConfirmDialog] = useState<'send' | 'delete' | 'convert' | 'accept' | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [downloadingEml, setDownloadingEml] = useState(false);

  // Items i ny adresse-model ligger på quoteAddress.items + pickupLines[].items + dropoffLines[].items.
  // Saml dem til prislinjer så Pris-tabellen viser dem sammen med eventuelle eksplicitte priceLines.
  const displayPriceLines = useMemo(() => {
    if (!quote) return [] as QuotePriceLine[];
    const lines: QuotePriceLine[] = [...(quote.priceLines || [])];
    if (quote.useNewAddressModel) {
      const allItems = [
        ...((quote.quoteAddress?.items) || []),
        ...((quote.pickupLines || []).flatMap(l => l.items || [])),
        ...((quote.dropoffLines || []).flatMap(l => l.items || [])),
      ];
      for (const it of allItems) {
        if (typeof it.unitPrice === 'number') {
          lines.push({
            description: it.description || it.productName || 'Linje',
            amount: it.unitPrice,
            quantity: it.quantity || 1,
            unitName: it.unitName ?? null,
          } as QuotePriceLine);
        }
      }
    }
    return lines;
  }, [quote]);

  // Skjul movement-linjer der hverken har adresse-data eller items — undgår tomme "—"-pladsholdere
  const nonEmpty = (l: { placeName?: string | null; address?: string; city?: string | null; items?: unknown[] }) =>
    !!(l.placeName || l.address || l.city || (l.items && l.items.length));
  const visiblePickupLines = useMemo(
    () => (quote?.pickupLines || []).filter(nonEmpty),
    [quote],
  );
  const visibleDropoffLines = useMemo(
    () => (quote?.dropoffLines || []).filter(nonEmpty),
    [quote],
  );

  if (isLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (error || !quote) return <Alert severity="error">Tilbud ikke fundet</Alert>;

  const status = quote.status as QuoteStatus;
  const cfg = statusConfig[status] || { label: status, color: 'default' as const };
  const quoteAddressItems = quote.quoteAddress?.items || [];

  const handleSend = async () => {
    setConfirmDialog(null);
    try {
      await sendQuote.mutateAsync(quote.id);
      setSnackbar({ open: true, message: 'Tilbud sendt!', severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke sende tilbud';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDownloadEml = async () => {
    setDownloadingEml(true);
    try {
      await api.downloadQuoteEml(quote.id, quote.quoteNumber);
      setConfirmDialog(null);
      setSnackbar({ open: true, message: 'Mail åbnet i din mail-klient. Tryk Send for at afsende.', severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke hente .eml-fil';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setDownloadingEml(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDialog(null);
    try {
      await deleteQuote.mutateAsync(quote.id);
      navigate('/tilbud');
    } catch {
      setSnackbar({ open: true, message: 'Kunne ikke slette tilbud', severity: 'error' });
    }
  };

  const handleConvert = async () => {
    setConfirmDialog(null);
    try {
      const result = await convertQuote.mutateAsync(quote.id);
      setSnackbar({ open: true, message: `Ordre oprettet: ${result.doraCargoRef}`, severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke konvertere tilbud';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleAccept = async () => {
    setConfirmDialog(null);
    try {
      const result = await acceptQuote.mutateAsync(quote.id);
      const n = result.templateIds.length;
      setSnackbar({ open: true, message: `${n} ordreskabelon${n === 1 ? '' : 'er'} oprettet i DORA`, severity: 'success' });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke markere tilbud som accepteret';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  // Info chips for header area
  const infoChips: Array<{ label: string; color?: 'default' | 'primary' | 'secondary' }> = [];
  if (quote.cargoType) infoChips.push({ label: cargoTypeLabels[quote.cargoType] || quote.cargoType });
  if (quote.adr) infoChips.push({ label: 'ADR', color: 'secondary' });
  if (quote.minTemperature != null || quote.maxTemperature != null) {
    infoChips.push({ label: `${quote.minTemperature ?? ''}/${quote.maxTemperature ?? ''}°C` });
  }

  return (
    <Box>
      {/* Sticky header — stays visible while scrolling */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: 'background.default',
          pt: 1,
          pb: 1.5,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tilbud')} size="small">Tilbage</Button>
              <Typography variant="h5" fontWeight={600}>#{quote.quoteNumber}</Typography>
              <Chip label={cfg.label} color={cfg.color} variant={status === 'CONVERTED' ? 'outlined' : 'filled'} />
              <Typography variant="body2" fontWeight={500}>
                {quote.customerName}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary">
                {fmt(quote.totalAmount, quote.currency)}
              </Typography>
            </Box>
            {/* Timeline inline */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', ml: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">Oprettet {fmtDateTime(quote.createdAt)}</Typography>
              {quote.sentAt && <><ArrowForwardIcon sx={{ fontSize: 12, color: 'text.disabled' }} /><Typography variant="caption" color="text.secondary">Sendt {fmtDateTime(quote.sentAt)}</Typography></>}
              {quote.acceptedAt && <><ArrowForwardIcon sx={{ fontSize: 12, color: 'success.main' }} /><Typography variant="caption" color="success.main">Accepteret {fmtDateTime(quote.acceptedAt)}</Typography></>}
              {quote.rejectedAt && <><ArrowForwardIcon sx={{ fontSize: 12, color: 'error.main' }} /><Typography variant="caption" color="error.main">Afvist {fmtDateTime(quote.rejectedAt)}</Typography></>}
              {quote.convertedAt && <><ArrowForwardIcon sx={{ fontSize: 12, color: 'success.main' }} /><Typography variant="caption" color="success.main">Konverteret {fmtDateTime(quote.convertedAt)}</Typography></>}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
            <Button startIcon={<PictureAsPdfIcon />} onClick={() => api.downloadQuotePdf(quote.id, quote.quoteNumber)} size="small">PDF</Button>
            <Button
              size="small"
              onClick={async () => {
                try {
                  const copy = await api.copyQuote(quote.id);
                  navigate(`/tilbud/${copy.id}/rediger`);
                } catch (err: unknown) {
                  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Kunne ikke kopiere';
                  setSnackbar({ open: true, message: msg, severity: 'error' });
                }
              }}
            >
              Kopier
            </Button>
            {(status === 'DRAFT' || status === 'SENT' || status === 'ACCEPTED') && (
              <Button
                startIcon={<EditIcon />}
                onClick={() => navigate(quote.useNewAddressModel
                  ? `/tilbud/${quote.id}/rediger`
                  : `/tilbud/${quote.id}/rediger-klassisk`)}
                size="small"
              >
                Rediger
              </Button>
            )}
            {status === 'DRAFT' && (
              <>
                <Button startIcon={<SendIcon />} variant="contained" onClick={() => setConfirmDialog('send')} disabled={sendQuote.isPending} size="small">Send</Button>
                <Button startIcon={<DeleteIcon />} color="error" onClick={() => setConfirmDialog('delete')} size="small">Slet</Button>
              </>
            )}
            {status === 'SENT' && (
              <Button startIcon={<SendIcon />} variant="contained" onClick={() => setConfirmDialog('send')} disabled={sendQuote.isPending} size="small">Gensend</Button>
            )}
            {/* To DORA-knapper: synlige når tilbuddet kan konverteres/accepteres */}
            {['DRAFT', 'SENT', 'ACCEPTED'].includes(status) && (
              <>
                {(quote.doraCargoTemplateIds || []).length === 0 && (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => setConfirmDialog('accept')}
                    disabled={acceptQuote.isPending}
                    size="small"
                  >
                    Tilbud accepteret
                  </Button>
                )}
                <Button
                  startIcon={<SyncAltIcon />}
                  variant="contained"
                  color="success"
                  onClick={() => setConfirmDialog('convert')}
                  disabled={convertQuote.isPending}
                  size="small"
                >
                  Konvertér til ordre
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* DORA conversion banner */}
      {quote.doraCargoId && (
        <Alert severity="success" sx={{ mb: 2 }}>Konverteret til DORA-ordre: <strong>{quote.doraCargoRef || quote.doraCargoId}</strong></Alert>
      )}

      {/* DORA cargo template banner — templates oprettet via "Tilbud accepteret" */}
      {(quote.doraCargoTemplateIds || []).length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {(quote.doraCargoTemplateIds || []).length} ordreskabelon{(quote.doraCargoTemplateIds || []).length === 1 ? '' : 'er'} oprettet i DORA. Brug dem til at starte ordrer når datoerne er klar.
        </Alert>
      )}

      {/* Customer comment banner */}
      {quote.customerComment && (
        <Alert severity="info" sx={{ mb: 2 }}>Kundens kommentar: "{quote.customerComment}"</Alert>
      )}

      {/* Naeste skridt — kontekstuel CTA placeret prominent over splittet */}
      <NextStepCard
        status={status}
        quote={quote}
        onSend={() => setConfirmDialog('send')}
        onAccept={() => setConfirmDialog('accept')}
        onConvert={() => setConfirmDialog('convert')}
      />

      {/* Splitview: tilbud (venstre 8/12) + sticky aktivitetslog (hoejre 4/12) */}
      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
        {/* Left: Kunde + Transport */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Kunde</Typography>
              <Typography variant="body2"><strong>{quote.customerName}</strong></Typography>
              <Typography variant="body2">{quote.customerEmail}</Typography>
              {quote.customerPhone && <Typography variant="body2">{quote.customerPhone}</Typography>}
              {quote.customerNumber && <Typography variant="caption" color="text.secondary">Kundenr: {quote.customerNumber}</Typography>}
            </CardContent>
          </Card>

          {(quote.cargoType || quote.description || quote.adr || quote.note) && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Transport</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                  {infoChips.map((c, i) => <Chip key={i} label={c.label} size="small" variant="outlined" />)}
                </Box>
                {quote.description && <Typography variant="body2">{quote.description}</Typography>}
                {quote.description2 && <Typography variant="body2" color="text.secondary">{quote.description2}</Typography>}
                {quote.adr && quote.adrText && <Typography variant="caption">ADR: {quote.adrText}</Typography>}
                {quote.note && <Typography variant="body2" fontStyle="italic" color="text.secondary" sx={{ mt: 0.5 }}>Note: {quote.note}</Typography>}
              </CardContent>
            </Card>
          )}

          {(quote.terms || quote.validUntil) && (
            <Card>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>Vilkår</Typography>
                {quote.validUntil && <Typography variant="body2">Gyldigt til: {fmtDate(quote.validUntil)}</Typography>}
                {quote.terms && <Typography variant="body2" color="text.secondary">{quote.terms}</Typography>}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Center: Rute — for ny model: tilbudsadresse + afhentninger/bortkørsler */}
        <Grid item xs={12} sm={6}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {quote.useNewAddressModel ? (
                <>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Tilbudsadresse + bevægelser
                  </Typography>
                  {quote.quoteAddress && (
                    <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {quote.quoteAddress.placeName || 'Tilbudsadresse'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[quote.quoteAddress.address, [quote.quoteAddress.postcode, quote.quoteAddress.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                      </Typography>
                      {quote.quoteAddress.ref && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Ref: {quote.quoteAddress.ref}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {quoteAddressItems.length > 0 && (
                    <Box sx={{ mt: 1, mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
                        Arbejde på tilbudsadressen ({quoteAddressItems.length} {quoteAddressItems.length === 1 ? 'vare' : 'varer'})
                      </Typography>
                      {quoteAddressItems.map((it: any, i: number) => (
                        <Box key={`qai${i}`} sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="caption">{it.description || it.productName || 'Linje'}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {it.quantity != null ? `${it.quantity} ${it.unitName || ''}`.trim() : ''}
                            </Typography>
                          </Box>
                          {it.internalNote && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              {it.internalNote}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {visiblePickupLines.length > 0 && (
                    <>
                      <Typography variant="caption" fontWeight={700} color="success.main" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        Leveringer TIL ({visiblePickupLines.length})
                      </Typography>
                      {visiblePickupLines.map((l, i) => (
                        <Box key={`pl${i}`} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                          <PlaceIcon sx={{ fontSize: 16, color: 'success.main', mt: 0.25 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {[l.placeName, l.address].filter(Boolean).join(', ') || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[l.postcode, l.city].filter(Boolean).join(' ')}
                              {l.date ? ` · ${l.date}` : ''}
                              {l.time ? ` ${l.time}` : ''}
                            </Typography>
                            {(l.description || l.weight) && (
                              <Typography variant="caption" color="text.secondary">
                                {l.description}{l.weight ? ` · ${l.weight}kg` : ''}
                              </Typography>
                            )}
                            {(l.items || []).map((it: any, idx: number) => (
                              <Box key={`pli${idx}`} sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                  <Typography variant="caption">{it.description || it.productName || 'Linje'}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                    {it.quantity != null ? `${it.quantity} ${it.unitName || ''}`.trim() : ''}
                                  </Typography>
                                </Box>
                                {it.internalNote && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                    {it.internalNote}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </>
                  )}
                  {visibleDropoffLines.length > 0 && (
                    <>
                      <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        Bortkørsler FRA ({visibleDropoffLines.length})
                      </Typography>
                      {visibleDropoffLines.map((l, i) => (
                        <Box key={`dl${i}`} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                          <PlaceIcon sx={{ fontSize: 16, color: 'error.main', mt: 0.25 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {[l.placeName, l.address].filter(Boolean).join(', ') || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {[l.postcode, l.city].filter(Boolean).join(' ')}
                              {l.date ? ` · ${l.date}` : ''}
                              {l.time ? ` ${l.time}` : ''}
                            </Typography>
                            {(l.description || l.weight) && (
                              <Typography variant="caption" color="text.secondary">
                                {l.description}{l.weight ? ` · ${l.weight}kg` : ''}
                              </Typography>
                            )}
                            {(l.items || []).map((it: any, idx: number) => (
                              <Box key={`dli${idx}`} sx={{ mt: 0.25 }}>
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  • {it.description || it.productName || 'Linje'}
                                  {it.quantity != null && ` · ${it.quantity} ${it.unitName || ''}`.trim()}
                                </Typography>
                                {it.internalNote && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1.5, fontStyle: 'italic' }}>
                                    {it.internalNote}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Rute{quote.loads && quote.loads.length > 1 ? `r (${quote.loads.length} læs)` : ''}
              </Typography>

              {(quote.loads && quote.loads.length > 0
                ? quote.loads
                : [{ pickups: quote.pickups, dropoffs: quote.dropoffs, cargoType: quote.cargoType }]
              ).map((load, loadIdx, arr) => (
                <Box key={loadIdx} sx={{ mb: loadIdx < arr.length - 1 ? 2 : 0, pb: loadIdx < arr.length - 1 ? 2 : 0, borderBottom: loadIdx < arr.length - 1 ? '1px dashed' : 'none', borderColor: 'divider' }}>
                  {arr.length > 1 && (
                    <Typography variant="caption" fontWeight={700} color="primary.dark" sx={{ display: 'block', mb: 0.5 }}>
                      Læs {loadIdx + 1}
                      {load.cargoType && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>· {load.cargoType}</Typography>}
                    </Typography>
                  )}

                  {/* Pickups */}
                  {(load.pickups || []).map((stop, i) => (
                    <Box key={`p${i}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}>
                      <PlaceIcon sx={{ fontSize: 16, color: 'success.main', mt: 0.25 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {[stop.place?.placeName, stop.place?.address].filter(Boolean).join(', ') || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[stop.place?.postcode, stop.place?.city].filter(Boolean).join(' ')}
                          {stop.place?.country && stop.place.country !== 'DK' ? ` ${stop.place.country}` : ''}
                          {stop.date ? ` · ${stop.date}` : ''}
                          {stop.time ? ` ${stop.time}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {stop.loadmeters && <Typography variant="caption" color="text.secondary">LDM: {stop.loadmeters}</Typography>}
                          {stop.weight && <Typography variant="caption" color="text.secondary">Vægt: {stop.weight}kg</Typography>}
                          {stop.ref && <Typography variant="caption" color="text.secondary">Ref: {stop.ref}</Typography>}
                        </Box>
                      </Box>
                    </Box>
                  ))}

                  {/* Arrow separator */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
                    <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled', transform: 'rotate(90deg)' }} />
                  </Box>

                  {/* Dropoffs */}
                  {(load.dropoffs || []).map((stop, i) => (
                    <Box key={`d${i}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 0.75 }}>
                      <PlaceIcon sx={{ fontSize: 16, color: 'error.main', mt: 0.25 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {[stop.place?.placeName, stop.place?.address].filter(Boolean).join(', ') || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[stop.place?.postcode, stop.place?.city].filter(Boolean).join(' ')}
                          {stop.place?.country && stop.place.country !== 'DK' ? ` ${stop.place.country}` : ''}
                          {stop.date ? ` · ${stop.date}` : ''}
                          {stop.time ? ` ${stop.time}` : ''}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {stop.loadmeters && <Typography variant="caption" color="text.secondary">LDM: {stop.loadmeters}</Typography>}
                          {stop.weight && <Typography variant="caption" color="text.secondary">Vægt: {stop.weight}kg</Typography>}
                          {stop.ref && <Typography variant="caption" color="text.secondary">Ref: {stop.ref}</Typography>}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Pris */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Pris</Typography>
              {displayPriceLines.length > 0 ? (
                <Table size="small" sx={{ '& td': { py: 0.5, px: 1, border: 0 } }}>
                  <TableBody>
                    {displayPriceLines.map((line: QuotePriceLine, i: number) => (
                      <TableRow key={i}>
                        <TableCell sx={{ pl: 0 }}>{line.description}</TableCell>
                        <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {line.quantity > 1 ? `${line.quantity} × ` : ''}{fmt(line.amount, quote.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ pl: 0, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}><strong>Total</strong></TableCell>
                      <TableCell align="right" sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body1" fontWeight={700} color="primary">{fmt(quote.totalAmount, quote.currency)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">Ingen prislinjer</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
          </Grid>
        </Grid>

        {/* Hoejre col: sticky aktivitetslog som alltid er synlig under scroll */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
            <QuoteActivityLog quoteId={quote.id} />
          </Box>
        </Grid>
      </Grid>

      {/* Confirm dialogs */}
      <Dialog open={confirmDialog === 'send'} onClose={() => setConfirmDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{quote.status === 'SENT' ? 'Gensend tilbud' : 'Send tilbud'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tilbuddet sendes til <strong>{quote.customerEmail}</strong> med PDF. Vælg sendemåde:
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Send via systemet</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Mailen sendes direkte fra serveren med PDF vedhæftet. Hurtigst, men kan ende i kundens spam.
              </Typography>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={sendQuote.isPending}
                size="small"
                fullWidth
              >
                {sendQuote.isPending ? <CircularProgress size={20} /> : (quote.status === 'SENT' ? 'Gensend via systemet' : 'Send via systemet')}
              </Button>
            </Box>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>Åbn i min mail-klient</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Henter en .eml-fil — dobbeltklik for at åbne i Outlook/Apple Mail med tilbud + PDF klar. Send selv. Mailen ender oftere i indbakken.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleDownloadEml}
                disabled={downloadingEml}
                size="small"
                fullWidth
              >
                {downloadingEml ? <CircularProgress size={20} /> : 'Hent .eml + marker som sendt'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'delete'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Slet tilbud #{quote.quoteNumber}?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteQuote.isPending}>Slet</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'convert'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Konvertér til DORA-ordre?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Opretter {quote.loads?.length || 1} cargo{(quote.loads?.length || 1) === 1 ? '' : 's'} i DORA TMS med {quote.priceLines.length} prislinje{quote.priceLines.length !== 1 ? 'r' : ''}.
            {quote.quoteType === 'RECURRING' && ' Da dette er et tilbagevendende tilbud, oprettes et ordreskema i stedet for én ordre.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="success" variant="contained" onClick={handleConvert} disabled={convertQuote.isPending}>
            {convertQuote.isPending ? <CircularProgress size={20} /> : 'Konvertér'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog === 'accept'} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>Marker som accepteret?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Opretter {quote.loads?.length || 1} ordreskabelon{(quote.loads?.length || 1) === 1 ? '' : 'er'} i DORA TMS — én pr. læs.
            Skabelonerne kan bruges til hurtigt at starte faktiske ordrer når datoer og detaljer er på plads.
            <br /><br />
            Navne: <em>Tilbud #{quote.quoteNumber} – {quote.customerName}{(quote.loads?.length || 1) > 1 ? ' – Læs 1/2/…' : ''}</em>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Annuller</Button>
          <Button color="success" variant="contained" onClick={handleAccept} disabled={acceptQuote.isPending}>
            {acceptQuote.isPending ? <CircularProgress size={20} /> : 'Opret ordreskabeloner'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

// ---- Naeste skridt-kort: tydeliggør hvad bruger skal goere nu ----
function NextStepCard({ status, quote, onSend, onAccept, onConvert }: {
  status: QuoteStatus;
  quote: { sentAt: string | null; doraCargoTemplateIds?: string[] | null; doraCargoId?: string | null; doraCargoRef?: string | null };
  onSend: () => void;
  onAccept: () => void;
  onConvert: () => void;
}) {
  const hasTemplates = (quote.doraCargoTemplateIds || []).length > 0;
  const daysSinceSent = quote.sentAt ? Math.floor((Date.now() - new Date(quote.sentAt).getTime()) / 86400000) : 0;

  let title = '';
  let body = '';
  let cta: { label: string; onClick: () => void; color?: 'primary' | 'success' } | null = null;
  let severity: 'info' | 'warning' | 'success' = 'info';

  if (status === 'DRAFT') {
    title = 'Tilbud er en kladde';
    body = 'Klar til at sendes til kunden via e-mail med PDF + accept-link.';
    cta = { label: 'Send tilbud', onClick: onSend, color: 'primary' };
  } else if (status === 'SENT' && daysSinceSent >= 3) {
    title = `Sendt for ${daysSinceSent} dage siden — følg op?`;
    body = 'Kunden har ikke svaret. Log et opkald eller en email i aktivitetsloggen nedenfor.';
    severity = 'warning';
  } else if (status === 'SENT') {
    title = 'Venter på kundens svar';
    body = 'Tilbuddet er sendt. Kunden kan acceptere via linket — eller du kan markere det manuelt.';
    cta = { label: 'Marker som accepteret', onClick: onAccept, color: 'success' };
  } else if (status === 'ACCEPTED' && hasTemplates) {
    title = 'Accepteret — skabelon klar i DORA';
    body = 'Konvertér skabelonen til en faktisk ordre når kunde-datoen kommer.';
    cta = { label: 'Konvertér til ordre', onClick: onConvert, color: 'success' };
    severity = 'success';
  } else if (status === 'ACCEPTED') {
    title = 'Accepteret';
    body = 'Konvertér til en faktisk DORA-ordre når du er klar.';
    cta = { label: 'Konvertér til ordre', onClick: onConvert, color: 'success' };
    severity = 'success';
  } else if (status === 'CONVERTED') {
    title = 'Konverteret til ordre';
    body = `Tilbuddet er konverteret til DORA-ordre ${quote.doraCargoRef || ''}.`;
    severity = 'success';
  } else if (status === 'REJECTED') {
    title = 'Tilbud afvist';
    body = 'Log gerne årsagen i aktivitetsloggen — nyttigt for fremtidige tilbud.';
    severity = 'warning';
  } else if (status === 'EXPIRED') {
    title = 'Tilbud udløbet';
    body = 'Tilbuddets gyldighed er udløbet. Opret nyt tilbud hvis kunden stadig er interesseret.';
    severity = 'warning';
  } else {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Alert
        severity={severity}
        action={cta ? (
          <Button color={cta.color} variant="contained" size="small" onClick={cta.onClick}>
            {cta.label}
          </Button>
        ) : undefined}
      >
        <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>{body}</Typography>
      </Alert>
    </Box>
  );
}

// ---- Aktivitets-log (interne notater på et tilbud) ----
function QuoteActivityLog({ quoteId }: { quoteId: string }) {
  const [activities, setActivities] = useState<QuoteActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<QuoteActivityKind>('NOTE');
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const list = await api.getQuoteActivities(quoteId);
      setActivities(list);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [quoteId]);

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.addQuoteActivity(quoteId, text, kind);
      setBody('');
      setKind('NOTE');
      await reload();
    } finally { setSaving(false); }
  };

  const remove = async (id: string) => {
    if (!confirm('Slet aktivitet?')) return;
    try {
      await api.deleteQuoteActivity(quoteId, id);
      setActivities(prev => prev.filter(a => a.id !== id));
    } catch { /* silent */ }
  };

  const kindLabels: Record<string, string> = { NOTE: 'Note', CALL: 'Opkald', EMAIL: 'Email', MEETING: 'Møde', OTHER: 'Andet' };
  const KIND_OPTIONS: Array<{ value: QuoteActivityKind; label: string }> = [
    { value: 'NOTE', label: 'Note' },
    { value: 'CALL', label: 'Opkald' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'MEETING', label: 'Møde' },
    { value: 'OTHER', label: 'Andet' },
  ];

  const relTime = (iso: string): string => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'lige nu';
    if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} dage siden`;
    return new Date(iso).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>Aktivitet</Typography>
          <Typography variant="caption" color="text.secondary">
            {activities.length === 0 ? '' : `${activities.length} ${activities.length === 1 ? 'log' : 'logs'}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
          {KIND_OPTIONS.map(o => (
            <Chip
              key={o.value}
              size="small"
              label={o.label}
              onClick={() => setKind(o.value)}
              color={kind === o.value ? 'primary' : 'default'}
              variant={kind === o.value ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={6}
            placeholder={
              kind === 'CALL' ? 'Hvad blev I enige om? Fx "Vender tilbage onsdag"' :
              kind === 'EMAIL' ? 'Hvad sendte du? Fx "Fulgte op med revideret PDF"' :
              kind === 'MEETING' ? 'Hvad blev besluttet? Fx "Kunde vil have 2 datoer ekstra"' :
              'Fx "Talte med ham — venter til næste uge"'
            }
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && body.trim()) submit(); }}
          />
          <Button variant="contained" size="small" disabled={saving || !body.trim()} onClick={submit} sx={{ alignSelf: 'flex-start' }}>
            Tilføj
          </Button>
        </Box>

        {loading ? (
          <Typography variant="body2" color="text.disabled">Henter…</Typography>
        ) : activities.length === 0 ? (
          <Box sx={{ py: 2, textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Ingen aktiviteter logget endnu
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Vælg type ovenfor og log første kontakt med kunden
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activities.map(a => (
              <Box key={a.id} sx={{ p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip size="small" label={kindLabels[a.kind] || a.kind} variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {a.user?.name || a.user?.email || '—'} · {relTime(a.createdAt)}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" onClick={() => remove(a.id)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{a.body}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
